import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles, mainSiteUrl } from '@/mocks/siteData';

export default function Navbar() {
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoryDropdownOpen(false);
    setGuidesDropdownOpen(false);
  }, [location.pathname]);

  const navBgClass = scrolled || !isHomePage
    ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/80'
    : 'bg-transparent';

  const linkColorClass = scrolled || !isHomePage
    ? 'text-foreground-700 hover:text-primary-600'
    : 'text-background-50 hover:text-background-100';

  const mobileLinkColorClass = 'text-foreground-700 hover:text-primary-600';

  const logoColor = scrolled || !isHomePage ? 'text-foreground-900' : 'text-background-50';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`flex items-center gap-2 transition-colors ${logoColor} hover:text-primary-500`}
          >
            <span className="font-heading text-xl font-bold whitespace-nowrap">
              Basetao<span className="text-primary-500">Sheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm transition-colors rounded-lg hover:bg-white/10 whitespace-nowrap ${linkColorClass}`}
            >
              Home
            </Link>

            <div ref={categoryRef} className="relative">
              <button
                onClick={() => {
                  setCategoryDropdownOpen(!categoryDropdownOpen);
                  setGuidesDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm transition-colors rounded-lg hover:bg-white/10 whitespace-nowrap ${linkColorClass}`}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${categoryDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-base"></i>
                </span>
              </button>

              {categoryDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-background-50 rounded-lg border border-background-200/80 shadow-lg py-2 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.iconClass} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={guidesRef} className="relative">
              <button
                onClick={() => {
                  setGuidesDropdownOpen(!guidesDropdownOpen);
                  setCategoryDropdownOpen(false);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm transition-colors rounded-lg hover:bg-white/10 whitespace-nowrap ${linkColorClass}`}
              >
                Guides
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${guidesDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-base"></i>
                </span>
              </button>

              {guidesDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 rounded-lg border border-background-200/80 shadow-lg py-2 max-h-[420px] overflow-y-auto">
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/${article.slug}`}
                      className="flex items-start gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                    >
                      <span className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden bg-background-200 mt-0.5">
                        <img src={article.image} alt="" className="w-full h-full object-cover" />
                      </span>
                      <span className="text-xs leading-snug line-clamp-2">{article.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-4 py-2 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line text-foreground-700' : (scrolled || !isHomePage ? 'ri-menu-line text-foreground-700' : 'ri-menu-line text-background-50')}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/80 px-4 py-3 max-h-[85vh] overflow-y-auto">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-800 rounded-lg"
          >
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-home-4-line text-base"></i></span>
            Home
          </Link>

          <div className="mt-2 px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wide">Categories</div>
          <div className="grid grid-cols-2 gap-1.5 px-1">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg"
              >
                <span className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img src={cat.image} alt="" className="w-full h-full object-cover" />
                </span>
                <span className="text-xs line-clamp-1">{cat.name}</span>
              </a>
            ))}
          </div>

          <div className="mt-3 px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wide">Guides</div>
          <div className="space-y-1">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/${article.slug}`}
                className="flex items-start gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg"
              >
                <span className="w-10 h-10 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img src={article.image} alt="" className="w-full h-full object-cover" />
                </span>
                <span className="text-xs leading-snug line-clamp-2">{article.title}</span>
              </Link>
            ))}
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center justify-center gap-2 mt-4 px-4 py-3 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg"
          >
            <i className="ri-shopping-bag-3-line"></i>
            Basetao Spreadsheet
          </a>
        </div>
      )}
    </nav>
  );
}